// Aggregates "needs attention" counts for the admin notification bell, entirely
// client-side from existing list endpoints (single-cluster scale is small).
// Actionable backlog (emergencies / bookings / complaints) is the current count;
// packages + visitor check-ins are counted as "new since the bell was last opened".

import { listAdminComplaints } from './complaints-api';
import { listAdminBookings } from './facilities-api';
import { listOpsEmergency, listOpsPackages, listScans } from './ops-api';

export interface AdminNotifications {
  emergenciesActive: number;
  bookingsPending: number;
  complaintsNew: number;
  packagesNew: number;
  scansNew: number;
  total: number;
}

const EMPTY: AdminNotifications = {
  emergenciesActive: 0,
  bookingsPending: 0,
  complaintsNew: 0,
  packagesNew: 0,
  scansNew: 0,
  total: 0,
};

/**
 * @param sinceIso ISO timestamp; packages/scans newer than this count as "new".
 * Each source is independently fault-tolerant — a 403 (staff lacks a permission)
 * or network error for one source contributes 0 rather than failing the whole bell.
 */
export async function getAdminNotifications(sinceIso: string): Promise<AdminNotifications> {
  const [emergenciesActive, bookingsPending, complaintsNew, packagesNew, scansNew] =
    await Promise.all([
      listOpsEmergency(100)
        .then((items) => items.filter((e) => e.status === 'ACTIVE').length)
        .catch(() => 0),
      listAdminBookings({ status: 'PENDING_APPROVAL', limit: 100 })
        .then((r) => r.items.length)
        .catch(() => 0),
      listAdminComplaints({ status: 'NEW', limit: 100 })
        .then((r) => r.items.length)
        .catch(() => 0),
      listOpsPackages(100)
        .then((items) => items.filter((p) => p.created_at > sinceIso).length)
        .catch(() => 0),
      listScans(100)
        .then((items) => items.filter((s) => s.scanned_at > sinceIso).length)
        .catch(() => 0),
    ]);

  return {
    emergenciesActive,
    bookingsPending,
    complaintsNew,
    packagesNew,
    scansNew,
    total: emergenciesActive + bookingsPending + complaintsNew + packagesNew + scansNew,
  };
}

export { EMPTY as EMPTY_NOTIFICATIONS };
